/**
 * Voice Memo Sidebar Component
 * Left sidebar showing packages/groups for navigation
 */

import { useState } from 'react';
import { Package, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from './voice-memo-status-badge';
import type { VoiceMemoPackage } from './types';

interface VoiceMemoSidebarProps {
  packages: VoiceMemoPackage[];
  selectedPackageId: string | null;
  onSelectPackage: (pkg: VoiceMemoPackage) => void;
}

export function VoiceMemoSidebar({
  packages,
  selectedPackageId,
  onSelectPackage
}: VoiceMemoSidebarProps) {
  return (
    <div className="h-full border-r border-border bg-sidebar flex flex-col">
      <div className="p-4 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground mb-1">Packages</h2>
        <p className="text-xs text-muted-foreground">{packages.length} groups</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          {packages.map(pkg => {
            const isSelected = selectedPackageId === pkg.id;
            const themes = Object.keys(pkg.packageMetadata.themes || {});

            return (
              <button
                key={pkg.id}
                onClick={() => onSelectPackage(pkg)}
                className={`w-full text-left p-3 rounded-md transition-colors ${
                  isSelected
                    ? 'bg-primary/10 border border-primary/20'
                    : 'hover:bg-muted/50 border border-transparent'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <h3 className="text-sm font-medium truncate">
                        {pkg.packageName || `${pkg.packageDate} Voice Memos`}
                      </h3>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {pkg.items.length} memos • {pkg.packageDate}
                    </p>
                  </div>
                  <ChevronRight
                    className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${
                      isSelected ? 'text-primary' : ''
                    }`}
                  />
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <StatusBadge status={pkg.status === 'analyzed' ? 'analyzed' : 'transcribed'} />
                </div>

                {themes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {themes.slice(0, 3).map(theme => (
                      <Badge key={theme} variant="secondary" className="text-xs">
                        {theme}
                      </Badge>
                    ))}
                    {themes.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{themes.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {pkg.packageMetadata.unifiedAnalysis?.summary && (
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                    {pkg.packageMetadata.unifiedAnalysis.summary}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
